import type { ComponentType, SVGProps } from 'react'

import type { TemplateCampaign } from '../../types'
import { BufferCommitLogo } from './BufferCommitLogo'
import { CreatorCampLogo, CreatorCampGrowthLogo } from './CreatorCampLogo'

/**
 * Visual branding applied on top of a campaign's banner. One-off per campaign;
 * keyed by the campaign's internal `name` so admins can wire new editions up
 * by creating a Notion record with a matching name.
 *
 * Add a new entry here when a marketing-led campaign needs distinct branding
 * (e.g. a fresh Creator Camp edition). Once a campaign ends, the entry can be
 * deleted along with any one-off assets.
 */
export type CampaignBranding = {
  /** Renders in place of the campaign title. Should accept `fill` for dark mode. */
  Logo: ComponentType<SVGProps<SVGSVGElement>>
  /** Accessible label for the logo (read by screen readers). */
  logoLabel: string
  /** Short tagline rendered next to the logo (e.g., "Consistency"). */
  tagline?: string
  /** Wraps the banner in the stamped (postage-style) border treatment. */
  stampedBorder?: boolean
  /** Optional promo line appended to the campaign description. */
  promoLink?: { label: string; href: string; trailingText?: string }
  color?: string
  /**
   * Locks the banner to a fixed dark palette regardless of the app's theme
   * (e.g. a campaign with a built-in dark gradient background). Add a
   * matching `.darkTheme` override in CampaignBanner.module.css.
   */
  forceDarkMode?: boolean
}

const CreatorCampConsistencyBranding: CampaignBranding = {
  Logo: CreatorCampLogo,
  logoLabel: 'Creator Camp',
  tagline: 'Consistency',
  stampedBorder: false,
  promoLink: {
    label: 'Creator Camp',
    href: 'https://buffer.com/resources/creator-camp-announcement/',
    trailingText: 'Learn more about ',
  },
}

const BRANDING_BY_CAMPAIGN_NAME: Record<string, CampaignBranding> = {
  // Legacy edition — kept so existing campaigns still render correctly.
  default: CreatorCampConsistencyBranding,
  'Creator Camp v3': CreatorCampConsistencyBranding,
  // Next edition (Growth-focused). Reuses the Creator Camp shell until the
  // Growth logo and palette ship — swap the Logo/tagline below once the
  // marketing assets land.
  'Creator Camp Growth': {
    ...CreatorCampConsistencyBranding,
    tagline: undefined,
    stampedBorder: true,
    Logo: CreatorCampGrowthLogo,
    color: 'var(--color-bg-brand-a-subtle)',
  },
  'Buffer Commit': {
    Logo: BufferCommitLogo,
    logoLabel: 'Buffer Commit',
    forceDarkMode: true,
  },
}

export const getCampaignBranding = (
  campaign: Pick<TemplateCampaign, 'name' | 'title'>,
): CampaignBranding | undefined => {
  const haystacks = [campaign.title ?? '', campaign.name]
  const matchedKey = Object.keys(BRANDING_BY_CAMPAIGN_NAME).find(
    (key) =>
      key !== 'default' && haystacks.some((value) => value.includes(key)),
  )
  return (
    (matchedKey && BRANDING_BY_CAMPAIGN_NAME[matchedKey]) ||
    BRANDING_BY_CAMPAIGN_NAME.default
  )
}
